import { useEffect, useLayoutEffect, useState } from 'react';

import {
  sendGlobalLayoutProps,
  sendGlobalErrorProps,
  waitForElement,
  ENVIRONMENTS,
} from 'cu-master-layout-prop-manager';
import { hasAuthParams, useAuth as useOidcAuth } from 'react-oidc-context';

import { apiServices } from '@/api';

const env = ENVIRONMENTS[import.meta.env.VITE_ENVIRONMENT_ID];

interface OidcInnerError {
  error?: string;
  error_description?: string | null;
  error_uri?: string | null;
  session_state?: string;
}

export function useAuth() {
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const auth = useOidcAuth();
  const token = auth.user?.access_token;

  useEffect(() => {
    if (!hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading && !hasTriedSignin) {
      void auth.signinRedirect();
      setHasTriedSignin(true);
      sendGlobalLayoutProps({
        token: token ?? '',
        env,
        activeLink: '/prospecting-list',
      });
    }
  }, [auth, hasTriedSignin, token]);

  useEffect(() => {
    if (!hasAuthParams() && auth.isAuthenticated && auth.user?.expired) {
      auth.signinSilent().catch(() => {
        void auth.signinRedirect();
      });
    }
  }, [auth, auth.user?.expired]);

  useLayoutEffect(() => {
    const services = Object.values(apiServices);

    const interceptors = services.map((service) =>
      service.interceptors.request.use((config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      }),
    );

    return () => {
      services.forEach((service, index) => {
        service.interceptors.request.eject(interceptors[index]);
      });
    };
  }, [token]);

  if (auth.isLoading) {
    return auth;
  }

  if (auth.error) {
    console.error('Auth error. Please check the defined auth credentials', auth.error);
    sendGlobalErrorProps({ errorType: 'generalError' });

    if (
      auth.error.message === 'login_required' ||
      (auth.error.innerError && (auth.error.innerError as OidcInnerError).error === 'login_required')
    ) {
      void auth.signinRedirect();
      return auth;
    }
  }

  if (!auth.isAuthenticated) {
    return auth;
  }

  if (auth.isAuthenticated && token) {
    waitForElement('#master_layout_logout_btn')
      .then((element) => {
        element.removeAttribute('href');
        element.addEventListener('click', () => {
          // Clear shared cuc-session cookie before navigating to IdP /connect/logout
          // so sibling .cucollaborate.com SPAs detect this logout on their next poll.
          document.cookie =
            'cuc-session=; Domain=.cucollaborate.com; Path=/; Secure; SameSite=Lax; Expires=Thu, 01 Jan 1970 00:00:00 GMT';
          void auth.signoutRedirect({
            id_token_hint: auth.user?.id_token,
          });
        });
      })
      .catch(console.error);

    sendGlobalLayoutProps({
      token,
      env,
      activeLink: '/prospecting-list',
    });
  }

  return auth;
}
